import React, { useEffect } from 'react';
import { Clock, X } from 'lucide-react';
import Tooltip from '../common/Tooltip';
import ScheduleConfigPanel from './ScheduleConfigPanel';
import { useModalDismiss } from '../../hooks/useModalDismiss';

interface ScheduleModalProps {
  isOpen: boolean;
  onClose: () => void;
  workflowId: number;
}

/**
 * ScheduleModal - A focused modal for configuring workflow schedules.
 *
 * Provides one-click access to schedule configuration without requiring
 * users to navigate through the Properties Panel.
 *
 * Dismiss behaviour (Escape + backdrop) goes through `useModalDismiss`,
 * which guards against the drag-select-overshoots-backdrop pattern
 * (AC-1593): a user drag-selecting the Run Every value could release the
 * mouse outside the panel, and a naive `onClick` check would dismiss the
 * modal mid-edit. The hook tracks both mousedown and mouseup targets.
 */
const ScheduleModal: React.FC<ScheduleModalProps> = ({
  isOpen,
  onClose,
  workflowId,
}) => {
  const { backdropProps } = useModalDismiss({ isOpen, onClose });

  useEffect(() => {
    if (!isOpen) return;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  if (!isOpen) {
    return null;
  }

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
      role="dialog"
      aria-modal="true"
      data-testid="modal-backdrop"
      {...backdropProps}
    >
      <div className="bg-white dark:bg-cap-dark rounded-lg shadow-xl w-full max-w-md mx-4 overflow-hidden">
        {/* Modal Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900">
          <div className="flex items-center space-x-2">
            <Clock className="w-5 h-5 text-primary-600" />
            <h2 className="text-lg font-semibold text-gray-900 dark:text-warm-white">Schedule Configuration</h2>
          </div>
          <Tooltip content="Close (Escape)">
          <button
            onClick={onClose}
            className="p-1 text-gray-400 hover:text-gray-600 dark:text-gray-400 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
          </Tooltip>
        </div>

        {/* Modal Body - ScheduleConfigPanel */}
        <div className="max-h-[60vh] overflow-y-auto">
          <ScheduleConfigPanel
            workflowId={workflowId}
            onScheduleUpdate={() => {
              // Optional: Could close modal on successful save
              // For now, let user see the success message and close manually
            }}
          />
        </div>

        {/* Modal Footer */}
        <div className="px-6 py-3 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-cap-dark border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 dark:bg-gray-900 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default ScheduleModal;
